import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Eye, FileText, Loader2, X } from 'lucide-react';
import { formatEuropeanDateTime } from '../utils/dateFormat';
import { OnboardingBackButton } from '../components/OnboardingBackButton';
import { reflowAgreementBody } from '../utils/reflowAgreementBody';
import { downloadOrShareBlob } from '../utils/downloadOrShareBlob';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Lists every Allure agreement signed by the current user. Each row
 * exposes two actions:
 *   - View → opens an in-page reader that fetches the full doc (including
 *     `body_snapshot`) and renders it. Owners can actually read their
 *     signed agreement without leaving the app.
 *   - PDF → downloads the PDF. Uses `window.open(blobUrl)` so Capacitor's
 *     Android WebView (which silently ignores anchor `download`) still
 *     hands the file off to the OS PDF viewer/saver.
 */
export default function MyAgreementsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState('');
  const [viewer, setViewer] = useState(null); // { agreement, loading, error }

  // Cold-start safe back button: if the user landed here directly (no
  // internal navigation yet — Android killed the app and re-opened it on
  // this URL, or a deep link hit us), navigate(-1) lands on about:blank
  // or the previous WebView entry and the user gets stuck. Use the
  // session-scoped `allureAppNavCount` sentinel App.js maintains — count
  // <= 1 means we're still on the first route mounted this session (cold
  // start). Fall back to the correct parent settings page in that case.
  const handleBack = useCallback(() => {
    let navCount = 1;
    try { navCount = Number(sessionStorage.getItem('allureAppNavCount') || '1'); } catch { /* ignore */ }
    if (navCount > 1) {
      navigate(-1);
      return;
    }
    const path = location.pathname || '';
    if (path.startsWith('/host/')) navigate('/host/settings', { replace: true });
    else if (path.startsWith('/venue/')) navigate('/venue/account-settings', { replace: true });
    else navigate('/', { replace: true });
  }, [location.pathname, navigate]);

  const fetchAgreements = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/agreements/my`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setItems(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your agreements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

  const handleView = async (agreement) => {
    setViewer({ agreement, loading: true, error: '', doc: null });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/agreements/${agreement.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setViewer({ agreement, loading: false, error: '', doc: res.data });
    } catch (err) {
      setViewer({
        agreement,
        loading: false,
        error: err.response?.data?.detail || 'Could not load this agreement',
        doc: null,
      });
    }
  };

  const closeViewer = () => setViewer(null);

  const handleDownload = async (agreement) => {
    setDownloadingId(agreement.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/agreements/${agreement.id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const filename = `${agreement.kind || 'allure'}-${(agreement.entity_name || 'agreement').replace(/[^a-z0-9-]+/gi, '-')}-${(agreement.id || '').slice(0, 8)}.pdf`;
      const result = await downloadOrShareBlob(blob, filename, { mimeType: 'application/pdf' });
      if (result.method === 'failed') {
        toast.error('Your device blocked the download. Please try again or contact support.');
      } else if (result.method === 'share' && !result.cancelled) {
        toast.success('Ready to save or share');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download PDF');
    } finally {
      setDownloadingId('');
    }
  };

  const titleFor = (agreement) => agreement.title
    || (agreement.kind === 'venue' ? 'Venue Partner Agreement' : 'Host Listing Agreement');

  return (
    <div
      data-testid="my-agreements-page"
      style={{
        minHeight: '100dvh',
        background: 'var(--app-bg)',
        padding: 'calc(env(safe-area-inset-top, 0px) + 20px) 20px calc(env(safe-area-inset-bottom, 0px) + 60px)',
      }}
    >
      <div style={{ marginBottom: 14 }}>
        <OnboardingBackButton
          onClick={handleBack}
          testId="my-agreements-back"
          ariaLabel="Go back"
        />
      </div>

      <h1 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 26, fontWeight: 700, color: 'var(--text)' }}>
        My Agreements
      </h1>
      <p style={{ margin: '6px 0 22px', fontSize: 14, color: 'var(--text-3)' }}>
        Signed copies of every Allure legal agreement you&apos;ve accepted. Tap
        <strong style={{ color: 'var(--text-2)' }}> View </strong>
        to read the agreement or
        <strong style={{ color: 'var(--text-2)' }}> PDF </strong>
        to download it.
      </p>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Loader2 size={20} className="address-loader-spin" />
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="my-agreements-empty"
          style={{
            padding: 28, borderRadius: 16, background: 'var(--surface)',
            border: '1px dashed var(--card-border)', textAlign: 'center', color: 'var(--text-3)', fontSize: 13,
          }}
        >
          <FileText size={20} style={{ marginBottom: 8, opacity: 0.6 }} />
          <p style={{ margin: 0 }}>You haven&apos;t signed any Allure agreements yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map((agreement) => (
            <div
              key={agreement.id}
              data-testid={`my-agreement-row-${agreement.id}`}
              style={{
                display: 'flex', gap: 12, padding: 14, borderRadius: 14,
                background: 'var(--surface)', border: '1px solid var(--card-border)',
                alignItems: 'center', flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 15, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {titleFor(agreement)}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {agreement.entity_name || agreement.kind} · {agreement.version} · signed {formatEuropeanDateTime(agreement.accepted_at)}
                </p>
              </div>
              <div style={{ display: 'inline-flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => handleView(agreement)}
                  data-testid={`my-agreement-view-${agreement.id}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                    borderRadius: 10, border: '1px solid var(--card-border)',
                    background: 'var(--surface)', color: 'var(--text)',
                    cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
                  }}
                >
                  <Eye size={13} />
                  View
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(agreement)}
                  disabled={downloadingId === agreement.id}
                  data-testid={`my-agreement-download-${agreement.id}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                    borderRadius: 10, border: '1px solid var(--card-border)',
                    background: 'var(--accent-surface-soft)', color: 'var(--accent-purple)',
                    cursor: downloadingId === agreement.id ? 'wait' : 'pointer',
                    fontSize: 12.5, fontWeight: 700,
                  }}
                >
                  {downloadingId === agreement.id ? <Loader2 size={13} className="address-loader-spin" /> : <Download size={13} />}
                  PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewer ? (
        <div
          data-testid="my-agreement-viewer"
          role="dialog"
          aria-modal="true"
          onClick={closeViewer}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'stretch',
            justifyContent: 'center', zIndex: 10000,
            padding: 'calc(env(safe-area-inset-top, 0px) + 20px) 12px calc(env(safe-area-inset-bottom, 0px) + 20px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 620, width: '100%', background: 'var(--surface)', borderRadius: 20,
              boxShadow: 'var(--premium-shadow-main)', display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--card-border)' }}>
              <FileText size={16} color="var(--accent-purple)" strokeWidth={2.1} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  data-testid="my-agreement-viewer-title"
                  style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 15, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {titleFor(viewer.agreement)}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {viewer.agreement.entity_name || viewer.agreement.kind} · {viewer.agreement.version} · signed {formatEuropeanDateTime(viewer.agreement.accepted_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={closeViewer}
                data-testid="my-agreement-viewer-close"
                aria-label="Close"
                style={{
                  background: 'var(--surface)', border: '1px solid var(--card-border)',
                  borderRadius: 10, width: 32, height: 32, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  color: 'var(--text)',
                }}
              >
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: 16, overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
              {viewer.loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 40, color: 'var(--text-3)' }}>
                  <Loader2 size={16} className="address-loader-spin" />
                  <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 13 }}>Loading agreement…</span>
                </div>
              ) : viewer.error ? (
                <p data-testid="my-agreement-viewer-error" style={{ color: 'var(--status-warning-text)', fontFamily: "'Inria Serif', serif", textAlign: 'center', padding: 30 }}>
                  {viewer.error}
                </p>
              ) : (
                <pre
                  data-testid="my-agreement-viewer-body"
                  style={{
                    margin: 0,
                    fontFamily: "'Inria Serif', serif",
                    fontSize: 13.5,
                    lineHeight: 1.55,
                    color: 'var(--text)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {reflowAgreementBody(viewer.doc?.body_snapshot) || 'This agreement has no stored body snapshot.'}
                </pre>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--card-border)' }}>
              <button
                type="button"
                onClick={closeViewer}
                data-testid="my-agreement-viewer-close-btn"
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 12,
                  border: '1px solid var(--card-border)', background: 'var(--surface)',
                  color: 'var(--text)', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => handleDownload(viewer.agreement)}
                disabled={downloadingId === viewer.agreement.id}
                data-testid="my-agreement-viewer-download-btn"
                style={{
                  flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 14px', borderRadius: 12, border: 'none',
                  background: 'var(--accent-purple)', color: '#FFF',
                  fontWeight: 700, fontSize: 13, cursor: downloadingId === viewer.agreement.id ? 'wait' : 'pointer',
                }}
              >
                {downloadingId === viewer.agreement.id ? <Loader2 size={14} className="address-loader-spin" /> : <Download size={14} />}
                Download PDF
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
